import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  ButtonProps,
  Flex,
  Heading,
  Image,
  PresentationalRadioGroup,
  Text,
} from '@opendoor/bricks/core';
import { useDevice } from '@opendoor/bricks/hooks/useMedia';
import { GroupOption } from '@opendoor/bricks/core/Input';

/* eslint-disable no-restricted-syntax */
import { Selection } from './ProxyImagesWizard';
import { ProxyImageCard } from './ProxyImageCard';
import { ProxyImage, MlsPhotos } from '../helpers/types';
/* eslint-enable no-restricted-syntax */

export enum MlsOption {
  yes = 'yes',
  no = 'no',
  skip = 'skip',
}

enum Step {
  MLS,
  SCORE,
}

const MLS_ID = 'mls-question';

const MLS_QUESTION_OPTIONS: GroupOption[] = [
  { label: 'Yes, it‘s the same', value: MlsOption.yes },
  { label: 'No, it‘s changed', value: MlsOption.no },
];

interface ProxyImagesWizardStepProps {
  title: string;
  options: Array<ProxyImage>;
  selection?: Selection;
  onSelect: (option?: ProxyImage, mlsOption?: MlsOption) => void;
  mlsPhoto?: MlsPhotos;
  goToNextStep: () => void;
}

/**
 * Renders the wizard step content in proxy images home conditions input flow
 * component-test-check-ignore
 */
export const ProxyImagesWizardStep: React.FC<ProxyImagesWizardStepProps> = ({
  title,
  options,
  onSelect,
  selection,
  mlsPhoto,
  goToNextStep,
}) => {
  const mlsPhotoProxy = mlsPhoto?.proxyImage?.[0];

  const analyticsSlug = title.split(' ').join('-').toLowerCase();
  const [step, setStep] = useState<Step>(() => (mlsPhotoProxy ? Step.MLS : Step.SCORE));
  const { isMobile } = useDevice();
  const proxyRef = useRef<(HTMLDivElement | null)[]>([]);

  /** Sorts the options in ascending order according to score from 1 - 5 */
  const sortedOptions = options.sort(function (a, b) {
    return a.score - b.score;
  });

  const titleToDisplay =
    title === 'Outdoor space & curb appeal' ? 'home‘s exterior' : title.toLowerCase();

  const heading =
    step === Step.MLS
      ? `Does your ${titleToDisplay} still look like this?`
      : `How would you describe your ${titleToDisplay}?`;

  /** Sets the mlsOption and goes to the SCORE step */
  const goToScoreStep = (mlsOption: MlsOption) => {
    onSelect(undefined, mlsOption);
    setStep(Step.SCORE);
  };

  /** Handled what happens when the customer clicks the "Next" button */
  const onNext = () => {
    if (step === Step.MLS) {
      if (selection?.mlsOption === MlsOption.yes) {
        if (!mlsPhotoProxy) {
          setStep(Step.SCORE);
        } else {
          onSelect(mlsPhotoProxy, MlsOption.yes);
          goToNextStep();
        }
      } else if (selection?.mlsOption === MlsOption.no) {
        goToScoreStep(MlsOption.no);
      }
    } else {
      if (selection?.proxyImage) {
        onSelect(selection?.proxyImage, selection?.mlsOption);
        goToNextStep();
      }
    }
  };

  const onSelectProxyImage = (image: ProxyImage) => onSelect(image, selection?.mlsOption);

  const disableNextButton = step === Step.MLS ? !selection?.mlsOption : !selection?.proxyImage;

  useEffect(() => {
    setStep(mlsPhotoProxy ? Step.MLS : Step.SCORE);
  }, [title, mlsPhotoProxy]);

  // This useEffect centers the carousel onto the middle card (score 3 card)
  // on every step of the wizard where none of the cards have been selected yet
  useEffect(() => {
    if (disableNextButton && isMobile) {
      proxyRef.current[2]?.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
      });
    }
  }, [disableNextButton, isMobile]);

  // This useEffect finds the selected element (if one has been selected) on each
  // step of the wizard and scrolls to them in the carousel
  useEffect(() => {
    if (!disableNextButton && isMobile) {
      const index = proxyRef.current.findIndex((item) => {
        return item?.getAttribute('data-selected') === 'true';
      });
      proxyRef.current[index]?.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
      });
    }
  }, [disableNextButton, isMobile, title]);

  return (
    <Flex height="100%" flexDir="column" justifyContent="space-between">
      <Flex
        flexDir="column"
        alignItems="center"
        minHeight="inherit"
        overflowX="hidden"
        width={['100%', null, '1196px']}
        maxWidth="100vw"
        mx="auto"
        py={[9, null, 10]}
        px={[0, 0, 5]}
        gridGap="8"
      >
        <Heading
          size="h2"
          as="h1"
          fontSize={['s3', null, 's4']}
          lineHeight={['s3', null, 's4']}
          fontWeight="bold"
          id={MLS_ID}
          textAlign="center"
          maxW="434px"
          px="4"
        >
          {heading}
        </Heading>
        {step === Step.MLS ? (
          <Flex flexDir="column" px={[5, null, 0]} gridGap="5">
            <Image
              src={mlsPhoto?.imageUrl || ''}
              alt=""
              aspectRatio="3/2"
              aria-hidden
              width="434px"
              objectFit="cover"
              borderRadius="roundedSquare"
            />
            <PresentationalRadioGroup
              analyticsName="cake-home-condition-mls-selection"
              name={`${analyticsSlug}${MLS_ID}`}
              onClick={(mlsOption) => {
                if (mlsOption === MlsOption.yes) {
                  onSelect(mlsPhoto?.proxyImage[0], MlsOption.yes);
                } else {
                  onSelect(undefined, MlsOption.no);
                }
              }}
              options={MLS_QUESTION_OPTIONS}
              hasContainer
              selectedValue={selection?.mlsOption}
              aria-labelledby={`${analyticsSlug}${MLS_ID}`}
            />
            <Flex justifyContent="center" gridGap="2">
              <Text>Wrong photo?</Text>
              <Button
                variant="inline-link"
                analyticsName="cake-home-condition-skip-mls"
                aria-label=""
                onClick={() => goToScoreStep(MlsOption.skip)}
                color="neutrals100"
              >
                Skip
              </Button>
            </Flex>
          </Flex>
        ) : (
          <Flex
            scrollSnapType="x mandatory"
            scrollBehavior="smooth"
            gridGap={4}
            alignSelf="center"
            maxWidth="100%"
            overflow={['scroll', null, 'hidden']}
            flexWrap={['nowrap', null, 'wrap']}
            justifyContent={['start', null, 'center']}
            css={{
              '&::-webkit-scrollbar': isMobile
                ? {
                    height: 0,
                    width: 0,
                  }
                : undefined,
            }}
            px="3"
            pt="6"
            pb="86px"
          >
            {sortedOptions.map((option, index) => (
              <Box
                key={index}
                scrollSnapAlign="center"
                ref={(el) => (proxyRef.current[index] = el)}
                data-selected={selection?.proxyImage?.id === option.id}
              >
                <ProxyImageCard
                  title={option.title}
                  description={option.description}
                  analyticsName={`${analyticsSlug}-${index}`}
                  imageUrl={option.imageUrl}
                  selected={selection?.proxyImage?.id === option.id}
                  hasAnyImageBeenSelected={!disableNextButton}
                  onClick={(e: React.TouchEvent | React.MouseEvent | null) => {
                    onSelectProxyImage(option);
                    if (!e) return;
                    if (isMobile) {
                      e.currentTarget.scrollIntoView({
                        behavior: 'smooth',
                        inline: 'center',
                      });
                    }
                  }}
                />
              </Box>
            ))}
          </Flex>
        )}
      </Flex>
      <NextButton
        disabled={disableNextButton}
        aria-label=""
        analyticsName="cake-home-conditions-next"
        onClick={onNext}
      />
    </Flex>
  );
};
const NextButton: React.FC<ButtonProps> = (buttonProps) => {
  const { isMobile } = useDevice();

  return (
    <Flex
      px="7"
      py="5"
      justifyContent="center"
      position="fixed"
      bottom="0"
      left="0"
      w="100%"
      boxShadow="z2"
      bgColor="neutrals0"
    >
      <Button {...buttonProps} width={isMobile ? '90%' : '256px'}>
        Next
      </Button>
    </Flex>
  );
};
